import { t as tr } from './i18n';
import { intlLocale } from './i18n';
import type { ActivityEvent } from './activity';
import type { DashOrder, DashRfq, Funnel, Savings } from './cockpit';
import type { RfqStat } from './panel';
import type { SiteRef, SupplierRef } from './types';

/**
 * Cifrele de pe panoul de control.
 *
 * Tot ce depinde de calendar se calculează **în browser**, din același motiv ca
 * ecranul de acțiuni: serverul rulează în UTC, iar o comandă dată pe 1 la ora
 * 01:00 ar cădea în luna trecută pentru cineva din România.
 */

/**
 * O cumpărătură: o comandă de achiziție emisă (neanulată), pusă în ziua din
 * care contează — prima livrare de pe aviz, altfel data dorită de pe comandă
 * (`orderCostDay`). Alegerea furnizorului nu e încă o cumpărătură.
 */
export interface Purchase {
  rfqId: string;
  site: SiteRef;
  /** 'AAAA-LL-ZZ' — ziua din care se socotește costul */
  day: string;
  /** lei fără TVA */
  total: number;
}

/**
 * Ce e hotărât, dar nu se poate pune încă într-o lună: furnizor ales fără
 * comandă emisă, sau comandă fără nicio dată de livrare. Nu intră în
 * „Cumpărat”; se arată separat, ca „Planificat”.
 */
export interface PlannedSpend {
  rfqId: string;
  site: SiteRef;
  /** 'order' = ales, comanda nu s-a emis; 'date' = comanda n-are dată de livrare */
  why: 'order' | 'date';
  /** lei fără TVA; null dacă nu s-a trecut nicio valoare */
  total: number | null;
}

/**
 * Ce trimite serverul pentru panou: numai ce nu se poate scoate din `/api/today`.
 * Cumpărăturile vin una câte una, negrupate — adunarea pe lună se face aici.
 */
/** O comandă care așteaptă aprobarea ta, pe panou. */
export interface ApprovalItem {
  orderId: string;
  rfqId: string;
  number: string;
  kind: 'order' | 'supplement';
  rfqTitle: string;
  supplier: string;
  siteName: string;
  /** lei fără TVA */
  valueLei: number;
  requestedBy: string;
  /** ISO 8601 */
  requestedAt: string;
  /** pasul de acum, din câți */
  step: number;
  steps: number;
}

/** Un necesar de pe șantier care așteaptă să fie preluat, pe panou. */
export interface RequisitionCard {
  id: string;
  number: string;
  title: string;
  siteName: string;
  author: string;
  /** ISO 8601 */
  createdAt: string;
  /** 'AAAA-LL-ZZ' sau null */
  neededBy: string | null;
  lines: number;
}

export interface DashboardData {
  /** comenzile pe care le poți aproba tu acum */
  toApprove?: ApprovalItem[];
  /** necesarul de pe șantier încă nepreluat */
  requisitions?: RequisitionCard[];
  /** șantiere deschise */
  sites: number;
  /** furnizori nearhivați */
  suppliers: number;
  /** comenzile cu zi cunoscută, inclusiv cele cu livrare în viitor */
  purchases: Purchase[];
  /** hotărât, dar încă fără lună */
  planned: PlannedSpend[];
  /** ISO 8601 — când s-a făcut fiecare cerere din fereastra graficului */
  requests: string[];
  /** ai ales furnizorul, dar comanda n-a plecat: cel mai vechi primul */
  toOrder: ToOrderRow[];
  /** toate comenzile emise (și suplimentările), pentru angajat, șantiere, categorii */
  orders: DashOrder[];
  /** cererile care încă cer ceva: deschise, sau alese fără comandă */
  rfqs: DashRfq[];
  /** diferența obținută, față de următoarea ofertă completă */
  savings: Savings;
  /** cererile din ultimele `FUNNEL_DAYS` zile, pe etape */
  funnel: Funnel;
  /** ultimele evenimente, cele mai noi primele */
  events: ActivityEvent[];
  /** bugetul pe șantier, lei; lipsă = nu e trecut */
  budgets: Record<string, number | null>;
  /** cererile din ultimele 25 de luni, câte un rând: perioada și șantierul se aleg în browser */
  stats?: RfqStat[];
}

/** Ziua locală, 'AAAA-LL-ZZ'. */
export function localDay(t: number): string {
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface SpendSplit {
  /** cumpărat până azi, inclusiv */
  bought: Purchase[];
  /** planificat: livrare în viitor, comandă fără dată, ales fără comandă */
  plannedTotal: number;
  plannedCount: number;
  /** câte din cele planificate n-au nicio valoare trecută */
  plannedUnknown: number;
}

/**
 * Desparte ce s-a cumpărat de ce urmează. O comandă cu livrarea pe 1 noiembrie
 * nu e „cumpărat” pe 8 octombrie; intră în luna ei când ajunge acolo.
 */
export function splitSpend(
  data: Pick<DashboardData, 'purchases' | 'planned'>,
  now: number = Date.now(),
): SpendSplit {
  const azi = localDay(now);
  const bought = data.purchases.filter((p) => p.day <= azi);
  const future = data.purchases.filter((p) => p.day > azi);
  const known = data.planned.filter((p) => p.total != null);
  return {
    bought,
    plannedTotal: future.reduce((n, p) => n + p.total, 0) + known.reduce((n, p) => n + p.total!, 0),
    plannedCount: future.length + data.planned.length,
    plannedUnknown: data.planned.length - known.length,
  };
}

/**
 * „De comandat”: cererea e de obicei închisă odată cu alegerea, deci dispare din
 * „Azi” — fără rândul ăsta, comanda nefăcută nu mai are unde să fie găsită.
 */
export interface ToOrderRow {
  rfq: { id: string; title: string; site: SiteRef };
  supplier: SupplierRef;
  /** ISO 8601, când s-a ales furnizorul */
  chosenAt: string;
  /** valoarea trecută la alegere, lei fără TVA; null dacă nu */
  total: number | null;
}

/** Câte luni arată graficul de cheltuieli. */
export const LUNI = 6;

export interface MonthSpend {
  /** 'AAAA-LL', luna locală */
  key: string;
  /** „sept." — eticheta de pe axă */
  label: string;
  /** „septembrie 2026” — în caseta de la mouse */
  name: string;
  total: number;
  /** câte comenzi au venit (sau trebuiau să vină) în luna asta */
  orders: number;
  /** câte cereri noi s-au făcut în luna asta */
  requests: number;
}

const cheieLuna = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/**
 * Cheltuielile pe ultimele `luni` luni, **inclusiv cele goale**.
 *
 * O lună fără cumpărături rămâne în șir, cu zero. Dacă am sări lunile goale,
 * graficul ar arăta o activitate neîntreruptă acolo unde a fost o pauză — e
 * exact minciuna pe care o spune un grafic cu categorii lipsă.
 */
export function spendByMonth(
  purchases: Purchase[],
  now: number = Date.now(),
  luni: number = LUNI,
  requests: string[] = [],
): MonthSpend[] {
  const azi = new Date(now);
  const sir: MonthSpend[] = [];
  for (let i = luni - 1; i >= 0; i--) {
    const d = new Date(azi.getFullYear(), azi.getMonth() - i, 1);
    sir.push({
      key: cheieLuna(d),
      label: d.toLocaleDateString(intlLocale(), { month: 'short' }),
      name: d.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' }),
      total: 0,
      orders: 0,
      requests: 0,
    });
  }

  const dupaCheie = new Map(sir.map((m) => [m.key, m]));
  for (const p of purchases) {
    const luna = dupaCheie.get(p.day.slice(0, 7));
    // Cumpărăturile mai vechi decât fereastra cad pe dinafară, intenționat.
    if (luna) {
      luna.total += p.total;
      luna.orders += 1;
    }
  }
  for (const at of requests) {
    const luna = dupaCheie.get(cheieLuna(new Date(at)));
    if (luna) luna.requests += 1;
  }
  return sir;
}

export interface MonthTrend {
  /** cumpărat luna asta, lei */
  now: number;
  /** cumpărat luna trecută, lei */
  prev: number;
  /** schimbarea în procente, rotunjită; null când luna trecută e zero (n-ai față de ce) */
  pct: number | null;
}

/** Luna asta față de luna trecută, din șirul lui `spendByMonth`. */
export function monthTrend(months: MonthSpend[]): MonthTrend {
  const now = months[months.length - 1]?.total ?? 0;
  const prev = months[months.length - 2]?.total ?? 0;
  return { now, prev, pct: prev > 0 ? Math.round(((now - prev) / prev) * 100) : null };
}

export interface SiteSpend {
  /** id-ul șantierului, sau null pentru rândul care adună coada */
  siteId: string | null;
  label: string;
  total: number;
}

/**
 * Cât s-a cumpărat pe fiecare șantier, cel mai scump primul.
 *
 * Peste `limit` șantiere, coada se adună într-un singur rând: un grafic cu
 * douăzeci de bare nu se mai citește, iar ultimele sunt oricum mărunțiș. Rândul
 * adunat nu dispare, ca totalul să rămână întreg.
 */
export function spendBySite(purchases: Purchase[], limit = 6): SiteSpend[] {
  const dupaSantier = new Map<string, SiteSpend>();
  for (const p of purchases) {
    const gasit = dupaSantier.get(p.site.id);
    if (gasit) gasit.total += p.total;
    else dupaSantier.set(p.site.id, { siteId: p.site.id, label: p.site.name, total: p.total });
  }

  const toate = [...dupaSantier.values()].sort(
    (a, b) => b.total - a.total || a.label.localeCompare(b.label, 'ro'),
  );
  if (toate.length <= limit) return toate;

  const capul = toate.slice(0, limit);
  const coada = toate.slice(limit);
  const rest = coada.reduce((n, s) => n + s.total, 0);
  return [...capul, { siteId: null, label: tr('încă {0} șantiere', coada.length), total: rest }];
}
